import { DynamicImage } from '../components/Image/Image';
import React, { Fragment } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  AddressBlock,
  ArticleBlock,
  ArticleBlockTypes,
  ArticleTypes,
  ContentWithLabelBlock,
  MediaArticleBlock,
  QuoteArticleBlock,
  RichTextArticleBlock,
  SliderAricleBlock,
  WeekdayWithTimeBlock,
} from '../types';
import { Carousel } from '../components/Carousel/Carousel';
import ChakraUIRenderer from 'chakra-ui-markdown-renderer';
import { getStrapiMedia } from '../lib/media';
import { Box, Button, Heading, Text } from '@chakra-ui/react';
import { None, Some } from '@sniptt/monads';
import clsx from 'clsx';
import { Patterns, formatDate } from '../utils/dateUtils';
import { parse } from 'date-fns';

interface BlockComponent {
  ['shared.content']: React.FC<RichTextArticleBlock>;
  ['shared.media']: React.FC<MediaArticleBlock>;
  ['shared.quote']: React.FC<QuoteArticleBlock>;
  ['shared.slider']: React.FC<SliderAricleBlock>;
  ['shared.addresse']: React.FC<AddressBlock>;
  ['shared.labeld-content']: React.FC<ContentWithLabelBlock>;
  ['shared.weekday-with-time']: React.FC<WeekdayWithTimeBlock>;
}

type Fooo<Type extends ArticleBlock<ArticleBlockTypes, any>> = {
  [K in Type as K['__component']]: React.FC<
    ArticleBlock<Type['__component'], Type>
  >;
};

const getBlockComponent = ({
  __component,
  ...rest
}: ArticleBlock<ArticleBlockTypes, object>) => {
  const BlockMap: Fooo<
    | RichTextArticleBlock
    | MediaArticleBlock
    | QuoteArticleBlock
    | SliderAricleBlock
    | AddressBlock
    | ContentWithLabelBlock
    | WeekdayWithTimeBlock
  > = {
    'shared.media': (props) => {
      const mediaProps = props as MediaArticleBlock;

      return Some(mediaProps)
        .andThen((m) => (m.file.data == null ? None : Some(m.file.data)))
        .andThen((image) => {
          const mimeType = image.attributes.mime || '';
          if (mimeType.includes('image')) {
            return Some(<DynamicImage blur image={image} />);
          }
          return getStrapiMedia(image).map((url) => (
            <>
              Datei:
              <Button
                variant="ghost"
                as="a"
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                {image.attributes.name}
              </Button>
            </>
          ));
        })
        .unwrapOr(<Fragment />);
    },
    'shared.quote': (props) => {
      const quoteProps = props as QuoteArticleBlock;
      return (
        <q key={props.id} className={props.__component}>
          {quoteProps.body}
        </q>
      );
    },
    'shared.content': (props) => {
      const richProps = props as RichTextArticleBlock;
      return (
        <ReactMarkdown
          components={ChakraUIRenderer()}
          key={props.id}
          className={props.__component as ArticleBlockTypes}
        >
          {richProps.body}
        </ReactMarkdown>
      );
    },
    'shared.slider': (props) => (
      <Carousel autoPlay={4}>
        {(props as SliderAricleBlock).files.data.map((image) => (
          <DynamicImage
            alt={image.attributes.alternativeText}
            className="object-contain"
            blur
            placeholder="blur"
            key={image.id}
            image={image}
          />
        ))}
      </Carousel>
    ),
    'shared.addresse': (props) => {
      const addressProps = props as AddressBlock;
      return (
        <section>
          <div>{addressProps.Addressat}</div>
          <div>{addressProps.Strasse}</div>
          <div>
            <span>{addressProps.Plz} </span> <span>{addressProps.Stadt}</span>
          </div>
        </section>
      );
    },
    'shared.labeld-content': (props) => {
      const blockProps = props as ContentWithLabelBlock;

      return (
        <Box
          as="section"
          className={clsx(blockProps.__component as ArticleBlockTypes)}
        >
          <Heading as="h4" size="md" className="mb-2">
            {blockProps.label}
          </Heading>
          <ReactMarkdown components={ChakraUIRenderer()} key={blockProps.id}>
            {blockProps.content}
          </ReactMarkdown>
        </Box>
      );
    },
    'shared.weekday-with-time': (props) => {
      const weekdayProps = props as WeekdayWithTimeBlock;
      const from = parse(
        weekdayProps.from.split('.')[0],
        'HH:mm:ss',
        new Date()
      );
      const to = parse(weekdayProps.to.split('.')[0], 'HH:mm:ss', new Date());

      return (
        <Text
          as="section"
          className={clsx(weekdayProps.__component as ArticleBlockTypes)}
        >
          {weekdayProps.day} {formatDate(from, Patterns.TIME_ONLY_NO_UNIT)} -{' '}
          {formatDate(to, Patterns.TIME_ONLY)}
        </Text>
      );
    },
  };

  const Block = BlockMap[__component] as BlockComponent[typeof __component];

  // @ts-ignore
  return <Block __component={__component} {...rest} />;
};

function BlockManager({ blocks }: { blocks: ArticleTypes[] }) {
  return <section>{blocks.map(getBlockComponent)}</section>;
}

export { BlockManager };
